<script lang="ts" setup>
import { ref, onMounted, onUnmounted } from 'vue'
import RoomieToast from '@lahaus-roomie/roomie/src/components/RoomieToast/index.vue'
import { useI18n } from 'vue-i18n'
import type { Props } from './types'
import { SCREEN } from '@/utils/segment'
import AppChatWindow from '@/components/App/ChatV2/Window/index.vue'
import AppChatButton from '@/components/App/ChatV2/Button/index.vue'
import { useAppChatStore } from '@/stores/appChatStore'

const appChatStore = useAppChatStore()

const { t } = useI18n()

const props = withDefaults(defineProps<Props>(), {
  screen: SCREEN.PDP_INVESTORS,
  dataAttributes: {
    dataLh: { P: 'unknown', Z: 'unknown' }
  }
})

const isAppChatButtonVisible = ref(true)
const isChatErrorToastVisible = ref(false)
const appChatButton = ref<InstanceType<typeof AppChatButton> | null>(null)

let observer: IntersectionObserver | null = null

// Handle chat error and show toast
const handleChatError = () => {
  appChatStore.closeChatWindow()
  showChatErrorToast()
}

// Show chat error toast for 5 seconds
const showChatErrorToast = () => {
  isChatErrorToastVisible.value = true
  setTimeout(() => {
    isChatErrorToastVisible.value = false
  }, 5000)
}

// Handle intersection observer entry changes
const handleObservedEntry = (entry: IntersectionObserverEntry) => {
  isAppChatButtonVisible.value = !entry.isIntersecting
}

// Initialize the intersection observer
const initializeVisibilityObserver = () => {
  if (!props.hidersSelectors) return

  observer = new IntersectionObserver((entries) => {
    entries.forEach(handleObservedEntry)
  }, { threshold: 1 })

  props.hidersSelectors.forEach((selector) => {
    const element = document.querySelector(selector)
    if (element) observer?.observe(element)
  })
}

// Disconnect the intersection observer
const disconnectVisibilityObserver = () => {
  if (!props.hidersSelectors || !observer) return
  observer.disconnect()
}

// Lifecycle hooks
onMounted(() => {
  initializeVisibilityObserver()
})

onUnmounted(() => {
  disconnectVisibilityObserver()
})
</script>

<script lang="ts">
export default {
  name: 'AppChat'
}
</script>

<template>
  <section class="app-chat">
    <div class="flex flex-col">
      <Transition
        name="slide-left">
        <AppChatWindow
          v-if="appChatStore.isChatWindowOpen"
          bot-agent-name="Sam"
          :data-attributes="dataAttributes"
          bot-agent-image="https://assets.lahaus.com/static/images/pdp/SamPhoto.png?auto=compress,enhance,format"
          :screen
          :listing
          :initial-chat-properties="appChatStore.contactFormConfig"
          :show-form="appChatStore.showForm"
          :face-to-face-visit-enabled
          @close-chat="appChatStore.closeChatWindow()"
          @error="handleChatError" />
      </Transition>

      <Transition name="slide-right">
        <AppChatButton
          v-if="isAppChatButtonVisible"
          ref="appChatButton"
          class="self-end"
          :data-attributes="dataAttributes"
          bot-agent-image="https://assets.lahaus.com/static/images/pdp/BigSam.png?auto=compress,enhance,format"
          :bot-agent-name
          :listing
          :screen
          :selector-to-trigger-button-chips
          :face-to-face-visit-enabled />
      </Transition>
    </div>

    <RoomieToast
      v-if="isChatErrorToastVisible"
      id="app-chat-error-toast"
      class="app-chat__toast"
      :title="t('samGpt.errorToast.title')"
      :description="t('samGpt.errorToast.description')"
      :is-visible="isChatErrorToastVisible"
      variant="error"
      @toast-closed="isChatErrorToastVisible = false" />
  </section>
</template>

<style lang="scss">
.lh-roomie-v2 {
  .app-chat {
    &__toast {
      position: fixed;
      top: 50px;
      right: 0;
    }
  }
}
</style>
